
import type { AccessTokenData, User, UserServiceListParams } from '@empathyco/playboard-core-domain'
import type { DataTableColumn, DataTableOption, UserRoleManagementMessages } from '../models'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import DataTable from '../components/crud/data-table.component.vue'
import PaginationBar from '../components/crud/pagination-bar.component.vue'
import { UiConfirm } from '../components/keen-ui'
import { EpbBadge } from '../components/shared/epb-badge'
import { EpbText } from '../components/shared/epb-text'
import SearchBox from '../components/shared/search-box.component.vue'
import RoleAssignmentInstanceCondition from '../components/users/role-assignment-instance-condition.component.vue'
import RolePill from '../components/users/role-pill.component.vue'
import UserDetailsModal from '../components/users/user-details-modal.component.vue'
import UserEditModal from '../components/users/user-edit-modal.component.vue'
import UserInviteModal from '../components/users/user-invite-modal.component.vue'
import UsersFilters from '../components/users/users-filters.component.vue'
import { useAppLoading } from '../composables/use-app-loading'
import { useConditionValues } from '../composables/users/use-condition-values'
import { useUsers } from '../composables/users/use-users'
import { useUsersError } from '../composables/users/use-users-error'
import { mapGetters } from '../composables/utils'
import { AlignEnum, ColorEnum } from '../enums'
import { AUTH_CONSTS } from '../store/auth/auth.consts'
import { compileMessage } from '../utils/messages.utils'

export default defineComponent({
  name: 'UserRoleManagement',
  components: {
    DataTable,
    EpbBadge,
    EpbText,
    PaginationBar,
    RoleAssignmentInstanceCondition,
    RolePill,
    SearchBox,
    UiConfirm,
    UserDetailsModal,
    UserEditModal,
    UserInviteModal,
    UsersFilters,
  },
  setup(_props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const { tokenInfo } = mapGetters<AccessTokenData>(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
      tokenInfo: AUTH_CONSTS.getters.tokenInfo,
    })

    const {
      loading: { list: loading },
      data: { list: data },
      error: { list: error },
      list,
    } = useUsers()

    const { setLoading } = useAppLoading()

    const { getErrorMessage } = useUsersError()

    const { getConditionValues } = useConditionValues()

    const messages = ctx.proxy.$epb.messages.userRoleManagement as UserRoleManagementMessages

    const baseClass = 'epb-user-role-management'

    const pageSizeOptions = [10, 20, 50]

    const detailsModal = ref()

    const editModal = ref()

    const deleteDialog = ref()

    const page = ref<UserServiceListParams['page']>({
      start: 0,
      rows: pageSizeOptions[0],
    })

    const search = ref<UserServiceListParams['search']>({
      query: '',
    })

    const filters = ref<UserServiceListParams['filters']>({})

    const selectedUser = ref<User | undefined>(undefined)

    const listParams = computed<UserServiceListParams>(() => ({
      page: page.value,
      search: search.value,
      filters: filters.value,
    }))

    const multiInstance = computed(
      () =>
        getConditionValues('instance').filter(conditionValue => conditionValue !== '*').length > 1,
    )

    const columns = computed<Array<DataTableColumn>>(() => [
      {
        id: 'initials',
        width: '3.5rem',
        align: AlignEnum.CENTER,
      },
      {
        id: 'user',
        text: messages.fields.user.label,
      },
      ...(multiInstance.value
        ? [
            {
              id: 'instances',
              text: messages.fields.instances.label,
              width: '12rem',
            },
          ]
        : []),
      {
        id: 'roles',
        text: messages.fields.roles.label,
        width: '200%',
      },
      {
        id: 'options',
        width: '44px',
        align: AlignEnum.CENTER,
      },
    ])

    const tableData = computed<Array<User>>(
      () =>
        data.value?.results.map(result => {
          ;(result as any).options = !isYou(result)
            ? [
                {
                  id: 'edit',
                  text: messages.options.edit,
                },
                ...(result.roleAssignments.length
                  ? [
                      {
                        id: 'delete',
                        text: messages.options.delete,
                      },
                    ]
                  : []),
              ]
            : []
          return result
        }) ?? [],
    )

    function isYou(user: User): boolean {
      return user.id === Number(tokenInfo.value.user.id)
    }

    function onRowClick(user: User): void {
      selectedUser.value = user.clone()
      if (multiInstance.value) {
        detailsModal.value.open()
      } else {
        editModal.value.open()
      }
    }

    function onOption(option: DataTableOption, user: User): void {
      selectedUser.value = user.clone()
      switch (option.id) {
        case 'edit':
          editModal.value.open()
          break
        case 'delete':
          deleteDialog.value.open()
          break
      }
    }

    async function onDeleteDialogConfirm(): Promise<void> {
      setLoading(true)
      try {
        selectedUser.value.roleAssignments = []
        await selectedUser.value.save()
        emit('showSnackbar', messages.delete.success)
        refresh()
      } catch (error) {
        emit('showError', getErrorMessage(error))
      } finally {
        setLoading(false)
      }
    }

    function refresh(): Promise<void> {
      return list(listParams.value)
    }

    watch(
      page,
      () => {
        list(listParams.value)
      },
      {
        deep: true,
        immediate: true,
      },
    )

    watch(
      [search, filters],
      () => {
        if (page.value.start > 0) {
          page.value.start = 0
        } else {
          list(listParams.value)
        }
      },
      {
        deep: true,
      },
    )

    watch(error, value => {
      if (value) {
        emit('showError', getErrorMessage(value))
      }
    })

    return {
      ColorEnum,
      messages,
      baseClass,
      pageSizeOptions,
      loading,
      data,
      detailsModal,
      editModal,
      deleteDialog,
      selectedUser,
      page,
      search,
      filters,
      multiInstance,
      columns,
      tableData,
      isYou,
      compileMessage,
      onRowClick,
      onOption,
      onDeleteDialogConfirm,
      refresh,
    }
  },
})
